/**
 * Ações de copiar injetadas no HTML cru que o vue-markdown gera (tabela e bloco
 * de código), no estilo ChatGPT/Claude.ai.
 *
 * Funções puras de DOM: nenhuma conhece Vue nem `this`. Quem copia de fato entra
 * por parâmetro (`onCopy(texto, mensagemDeSucesso)`), porque o feedback de
 * sucesso/erro é responsabilidade do componente que monta o markdown — aqui só
 * mora a manipulação do DOM, que é o que tem teste próprio.
 */

// Ícone "copiar" (Feather/Lucide, stroke-based) usado nos botões injetados via DOM
// em tabela/bloco de código — precisa ser uma string SVG crua (não o componente Vue
// <icon-svg>) porque esses botões são criados fora do ciclo de render do Vue, direto
// no HTML cru que o vue-markdown gera.
const ICONE_COPIAR_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>'

const SELETOR_TABELA = '.ia-message-content table:not([data-copy-ready])'
const SELETOR_CODIGO = '.ia-message-content pre:not([data-copy-ready])'
const CLASSE_WRAPPER_TABELA = 'ia-table-wrapper'
const CLASSE_BOTAO_TABELA = 'ia-table-wrapper__copy-btn'
const CLASSE_BLOCO_CODIGO = 'ia-code-block'
const CLASSE_HEADER_CODIGO = 'ia-code-header'
const CLASSE_LINGUAGEM_CODIGO = 'ia-code-header__lang'
const CLASSE_BOTAO_CODIGO = 'ia-code-header__copy-btn'
const REGEX_LINGUAGEM = /language-(\S+)/

const LABEL_COPIAR_TABELA = 'Copiar tabela'
const LABEL_COPIAR_CODIGO = 'Copiar'
const LABEL_LINGUAGEM_GENERICA = 'Código'
const SUCESSO_TABELA = 'Tabela copiada'
const SUCESSO_CODIGO = 'Código copiado'

// Serializa uma <table> renderizada como TSV — cola certo em Excel/Sheets
// (linhas por \n, células por \t).
export function extrairTabelaComoTexto(table) {
  return Array.from(table.querySelectorAll('tr'))
    .map((linha) =>
      Array.from(linha.querySelectorAll('th, td'))
        .map((celula) => celula.textContent.trim())
        .join('\t')
    )
    .join('\n')
}

// `iconOnly` omite o <span> de texto visível (botão flutuante que sobrepõe
// conteúdo, ex: tabela) — title/aria-label seguem levando o label inteiro,
// então a acessibilidade não depende do texto na tela.
export function criarBotaoCopiar(
  className,
  label,
  onClick,
  { iconOnly = false } = {}
) {
  const botao = document.createElement('button')
  botao.type = 'button'
  botao.className = className
  botao.title = label
  botao.setAttribute('aria-label', label)
  botao.innerHTML = ICONE_COPIAR_SVG
  if (!iconOnly) {
    const labelEl = document.createElement('span')
    labelEl.textContent = label
    botao.appendChild(labelEl)
  }
  botao.addEventListener('click', onClick)
  return botao
}

// Header do bloco de código: linguagem à esquerda, botão de copiar à direita.
export function criarHeaderCodigo(pre, code, onCopy) {
  const match = code && code.className.match(REGEX_LINGUAGEM)
  const label = document.createElement('span')
  label.className = CLASSE_LINGUAGEM_CODIGO
  label.textContent = match ? match[1] : LABEL_LINGUAGEM_GENERICA

  const header = document.createElement('div')
  header.className = CLASSE_HEADER_CODIGO
  header.appendChild(label)
  header.appendChild(
    criarBotaoCopiar(CLASSE_BOTAO_CODIGO, LABEL_COPIAR_CODIGO, () => {
      onCopy(code ? code.textContent : pre.textContent, SUCESSO_CODIGO)
    })
  )
  return header
}

export function envolverBlocoCodigo(pre, onCopy) {
  pre.dataset.copyReady = 'true'
  const code = pre.querySelector('code')
  const wrapper = document.createElement('div')
  wrapper.className = CLASSE_BLOCO_CODIGO
  pre.parentNode.insertBefore(wrapper, pre)
  wrapper.appendChild(criarHeaderCodigo(pre, code, onCopy))
  wrapper.appendChild(pre)
}

// Injeta wrapper + botão em cada <table> ainda não processada dentro de `root`.
// O marcador `data-copy-ready` é o que impede duplicar numa segunda passada.
export function injetarBotaoTabela(root, onCopy) {
  const tabelas = root.querySelectorAll(SELETOR_TABELA)
  tabelas.forEach((table) => {
    table.dataset.copyReady = 'true'
    const wrapper = document.createElement('div')
    wrapper.className = CLASSE_WRAPPER_TABELA
    table.parentNode.insertBefore(wrapper, table)
    wrapper.appendChild(table)
    wrapper.appendChild(
      criarBotaoCopiar(
        CLASSE_BOTAO_TABELA,
        LABEL_COPIAR_TABELA,
        () => {
          onCopy(extrairTabelaComoTexto(table), SUCESSO_TABELA)
        },
        { iconOnly: true }
      )
    )
  })
}

export function injetarBotaoCodigo(root, onCopy) {
  const blocos = root.querySelectorAll(SELETOR_CODIGO)
  blocos.forEach((pre) => envolverBlocoCodigo(pre, onCopy))
}
